import {
  Button,
  ButtonDesigns,
  ButtonTypes,
  getFontCss,
  HoverableLinkSizes,
  Popup,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import BmoCobrandWhatsNew from 'src/assets/svgs/Bmo-Cobrand-Whats-New.svg'
import PNCWelcomePopupSVG from 'src/assets/svgs/PNC-Extend-Cobrand.svg'
import AppName from 'src/components/AppName/AppName'
import ResourceCenterLink from 'src/components/ResourceCenterLink/ResourceCenterLink'
import { CoBrandAbbreviations, CoBrandFullNames } from 'src/constants'
import { CoBrands } from 'src/enums'
import { useTranslate } from 'src/localization/Trns'
import { createSetIsBrandSpecificWelcomePopupOpen } from 'src/redux/actions/app.actions'
import { css } from 'styled-components'

interface CoBrandWelcomePopupProps {
  cookieFunction: (v: boolean) => void
  popupOpen: boolean
  brandName: CoBrands
}

const CoBrandWelcomePopup: React.FC<CoBrandWelcomePopupProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const dispatch = useDispatch()

  const closePopup = (): void => {
    dispatch(createSetIsBrandSpecificWelcomePopupOpen(false))
    props.cookieFunction(false)
  }

  useEffect(() => {
    if (props.popupOpen) {
      dispatch(createSetIsBrandSpecificWelcomePopupOpen(true))
    }
  }, [])

  // TODO: Improve this so that it's a component with just the brand logo interchangeable instead of an entire image for the banner
  const imgSrc = (): string | undefined => {
    switch (props.brandName) {
      case CoBrands.Pnc:
        return PNCWelcomePopupSVG
      case CoBrands.Bmo:
        return BmoCobrandWhatsNew
      default:
        return undefined
    }
  }

  return (
    <Popup onClickClose={closePopup} showCloseButton>
      <span
        css={css`
          display: flex;
          flex-direction: column;
          position: relative;
        `}
      >
        <span
          css={css`
            ${getFontCss(
              32,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
            margin-bottom: 40px;
          `}
        >
          {translate('Welcome to ${appName}', {
            appName: <AppName />,
          })}
        </span>
        <span
          css={css`
            margin-bottom: 36px;
          `}
        >
          <img
            src={imgSrc()}
            alt={`${CoBrandAbbreviations[props.brandName]} + Extend`}
          />
        </span>
        <span
          css={css`
            ${getFontCss(
              20,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
            margin-bottom: 32px;
          `}
        >
          {translate("We've partnered with ${coBrandAbbreviation}!", {
            coBrandAbbreviation: CoBrandAbbreviations[props.brandName],
          })}
        </span>
        <span
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
            margin-bottom: 24px;
          `}
        >
          {translate(
            "We're excited to announce our partnership with ${coBrandFullName} and are now offering the Extend app to ${coBrandAbbreviation} customers.",
            {
              coBrandFullName: CoBrandFullNames[props.brandName],
              coBrandAbbreviation: CoBrandAbbreviations[props.brandName],
            },
          )}
        </span>
        <span
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
            margin-bottom: 24px;
          `}
        >
          {translate(
            'For any questions, you can always visit our ${resourceCenter} for support and contact options.',
            {
              resourceCenter: (
                <ResourceCenterLink
                  size={HoverableLinkSizes.Large}
                  underline
                  regular
                />
              ),
            },
          )}
        </span>
        <span
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
            margin-bottom: 40px;
          `}
        >
          {translate('Happy Extending!')}
        </span>
        <Button
          css={css`
            width: 124px;
          `}
          design={ButtonDesigns.designCta}
          type={ButtonTypes.button}
          big={true}
          text={translate('Continue')}
          onClick={closePopup}
        />
      </span>
    </Popup>
  )
}

export default CoBrandWelcomePopup
